import React, { FC, useEffect, useState } from "react";
import { IntervalDay } from "../../Steps/AppointmentStep/AppointmentStep";
import { DayPickerItem } from "./DayPickerItem";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper";
import { Swiper as SwiperCore } from "swiper";
import { endOfMonth, startOfDay, startOfMonth } from "date-fns";
import { DayPickerCarouselArrow } from "./DayPickerCarouselArrow";

interface DayPickerProps {
  selectedDay: Date;
  selectedMonth: Date;
  handleDayChange: (day: Date) => void;
  handleMonthChange: (newMonth: Date) => void;
  interval: IntervalDay[];
  currentSlideIndex: number;
  setCurrentSlideIndex: (index: number) => void;
}



export const DayPicker: FC<DayPickerProps> = ({
  selectedDay,
  selectedMonth,
  handleDayChange,
  handleMonthChange,
  interval,
  currentSlideIndex,
  setCurrentSlideIndex,
}) => {
  const [swiperState, setSwiperState] = useState<SwiperCore>();


  const breakpoints = {
    0: {
      slidesPerView: 5,
      spaceBetween: 8,
      initialSlide: currentSlideIndex
    },
    768: {
      slidesPerView: 7,
      centeredSlides: true,
      centeredSlidesBounds: true,
      initialSlide: currentSlideIndex,
      spaceBetween: 8,
    },
  };
  
  useEffect(() => {
    if (swiperState) {
      const currentDay = interval[swiperState.activeIndex];
      if (currentDay.date.getTime() < selectedMonth.getTime()) {
        const index = interval.findIndex(
          (day) => selectedMonth.getTime() === day.date.getTime()
        );
        swiperState?.slideTo(index);
      }
      const lastDayOfMonth = startOfDay(endOfMonth(selectedMonth));
      if (currentDay.date.getTime() > lastDayOfMonth.getTime()) {
        const index = interval.findIndex(
          (day) => lastDayOfMonth.getTime() === day.date.getTime()
        );
        swiperState?.slideTo(index);
      }
    }
  }, [selectedMonth.getTime()]);

  useEffect(() => {
    const index = interval.findIndex(
      (day) => day.date.getTime() === selectedDay.getTime()
    );

    swiperState?.slideTo(index);
  }, [selectedDay.getTime()]);

  const onTransitionEnd = (swiper: SwiperCore) => {
    const currentDay = interval[swiper.activeIndex];
    const currentMonth = startOfMonth(currentDay.date);
    if (currentMonth.getTime() !== selectedMonth.getTime()) {
      handleMonthChange(currentMonth);
    }
    setCurrentSlideIndex(swiper.activeIndex);
  };

  const goToNextSlide = () => {
    if (swiperState) {
      const currentIndex = swiperState.activeIndex;
      for (let i = currentIndex + 1; i < interval.length; i++) {
        if (interval[i].hasAppointments) {
          swiperState!.slideTo(i);
          return;
        }
      }
    }
  };

  const goToPrevSlide = () => {
    if (swiperState) {
      const currentIndex = swiperState.activeIndex;
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (interval[i].hasAppointments) {
          swiperState!.slideTo(i);
          return;
        }
      }
    }
  };

  return (
    <div className="DayPicker">
      <Swiper
        breakpoints={breakpoints}
        freeMode={{ enabled: true, momentum: true, sticky: true }}
        modules={[FreeMode]}
        onTransitionEnd={onTransitionEnd}
        onSwiper={(swiper) => setSwiperState(swiper)}
        className="DayPicker__swiper"
      >
        {interval.map((date, i) => (
          <SwiperSlide key={i}>
            <DayPickerItem
              date={date}
              selectedDay={selectedDay}
              onClick={handleDayChange}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="DayPicker__nav">
        <DayPickerCarouselArrow
            onClick={goToPrevSlide}
            direction="prev"
            disabled={swiperState?.isBeginning}
        />
        <DayPickerCarouselArrow
            onClick={goToNextSlide}
            direction="next"
            disabled={swiperState?.isEnd}
        />
      </div>
    </div>
  );
};
